
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminData } from '@/hooks/useAdminData';
import AdminCard from '@/components/admin/AdminCard';
import AdminFilters from '@/components/admin/AdminFilters';
import AdminListHeader from '@/components/admin/AdminListHeader';
import AdminLoadingState from '@/components/admin/AdminLoadingState';
import AdminEmptyState from '@/components/admin/AdminEmptyState';
import AdminErrorState from '@/components/admin/AdminErrorState';
import AdminFormatDialog from '@/components/admin/AdminFormatDialog';
import AdminPageSelectionDialog from '@/components/admin/AdminPageSelectionDialog';
import { Button } from '@/components/ui/button';
import { FileText, Plus, RefreshCw, Download } from 'lucide-react';
import { printAdminsListPaginatedAsJPG, printAdminsListPaginatedAsPDF } from '@/utils/printing/adminsPaginatedPrinter';
import { toast } from 'sonner';

const RECORDS_PER_PAGE = 12; // Set to 12 as requested

const AdminsList = () => {
  const navigate = useNavigate();
  const {
    admins,
    isLoading,
    isRefreshing,
    loading,
    error,
    filterText,
    setFilterText,
    roleFilter,
    setRoleFilter,
    refreshAdmins,
    deleteAdmin,
    roles,
    initialized,
    initializeAdminData
  } = useAdminData();
  const [isRefreshingLocal, setIsRefreshingLocal] = useState(false);
  const [showPageDialog, setShowPageDialog] = useState(false);
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  const [selectedPages, setSelectedPages] = useState<{ start: number; end: number } | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Initialize admin data on component mount
  useEffect(() => {
    if (!initialized) {
      console.log('🚀 Component mounted, initializing admin data...');
      initializeAdminData();
    }
  }, [initialized, initializeAdminData]);

  const handleAddAdmin = () => {
    console.log('Navigating to admin form...');
    navigate('/admins/new');
  };
  
  const handleHistoryManagement = () => navigate('/admins/history');

  const handleRefresh = async () => {
    setIsRefreshingLocal(true);
    try {
      await refreshAdmins();
      toast.success('Admin list refreshed successfully');
    } catch (error) {
      console.error('Error refreshing admins:', error);
      toast.error('Failed to refresh admin list');
    } finally {
      setIsRefreshingLocal(false);
    }
  };

  const handleDeleteAdmin = async (id: string): Promise<boolean> => {
    try {
      await deleteAdmin(id);
      return true;
    } catch (error) {
      console.error('Error deleting admin:', error);
      return false;
    }
  };

  const handleDownloadClick = () => {
    if (!admins || admins.length === 0) {
      toast.error('No administrators to download');
      return;
    }
    // Always show page selection dialog first, regardless of page count
    setShowPageDialog(true);
  };

  const handlePageSelection = (startPage: number, endPage: number) => {
    setSelectedPages({ start: startPage, end: endPage });
    setShowPageDialog(false);
    setShowFormatDialog(true);
  };

  const handleFormatDownload = async (format: 'jpg' | 'pdf') => {
    if (admins && admins.length > 0) {
      try {
        setIsDownloading(true);
        const startPage = selectedPages?.start || 1;
        const endPage = selectedPages?.end || Math.ceil(admins.length / RECORDS_PER_PAGE);
        
        if (format === 'jpg') {
          await printAdminsListPaginatedAsJPG(admins, startPage, endPage);
        } else {
          await printAdminsListPaginatedAsPDF(admins, startPage, endPage);
        }
      } catch (error) {
        console.error('Error downloading admins list:', error);
        toast.error('Failed to download administrators list');
      } finally {
        setIsDownloading(false);
        setShowFormatDialog(false);
        setSelectedPages(null);
      }
    } else {
      toast.error('No administrators to download');
    }
  };

  const isFilterActive = !!filterText || roleFilter !== 'all-roles';

  // Show loading state while initializing
  if (!initialized || loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="space-y-2 mb-6">
          <h1 className="text-2xl font-bold">Administrators</h1>
          <p className="text-sm text-muted-foreground">Loading administrators with 5000+ capacity support...</p>
        </div>
        <AdminLoadingState />
      </div>
    );
  }

  if (error) {
    return <AdminErrorState error={error} onRetry={handleRefresh} />;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header Section */}
      <div className="space-y-2 mb-6">
        <h1 className="text-2xl font-bold">Administrators</h1>
        <p className="text-sm text-muted-foreground">
          Manage system administrators with enhanced 5000+ capacity ({admins.length} admins loaded)
        </p>
        
        {/* Buttons Row - Flex container with wrap */}
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Button 
            onClick={handleAddAdmin} 
            size="sm" 
            className="whitespace-nowrap flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add New Admin
          </Button>
          <Button 
            variant="outline" 
            onClick={handleHistoryManagement} 
            size="sm" 
            className="whitespace-nowrap"
          >
            <FileText className="h-4 w-4 mr-1" />
            History Management
          </Button>
          <Button 
            variant="outline" 
            onClick={handleDownloadClick} 
            size="sm" 
            className="whitespace-nowrap"
            disabled={isDownloading || !admins || admins.length === 0}
          >
            <Download className="h-4 w-4 mr-1" />
            {isDownloading ? 'Downloading...' : 'Download List'}
          </Button>
          <Button 
            variant="outline" 
            onClick={handleRefresh} 
            size="sm" 
            className="whitespace-nowrap"
            disabled={isRefreshingLocal || isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${(isRefreshingLocal || isRefreshing) ? 'animate-spin' : ''}`} />
            {isRefreshingLocal || isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Filters Section */}
      <AdminFilters 
        filterText={filterText}
        setFilterText={setFilterText}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
        roles={roles}
      />

      {/* Content Section with animation container */}
      {admins.length === 0 ? (
        <AdminEmptyState filterActive={isFilterActive} onAddAdmin={handleAddAdmin} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {admins.map((admin) => (
            <div key={admin.id} className="transform transition-all duration-300 hover:-translate-y-1">
              <AdminCard
                admin={admin}
                onDelete={handleDeleteAdmin}
              />
            </div>
          ))}
        </div>
      )}

      <AdminPageSelectionDialog
        isOpen={showPageDialog}
        onClose={() => setShowPageDialog(false)}
        totalRecords={admins.length}
        recordsPerPage={RECORDS_PER_PAGE}
        onDownload={handlePageSelection}
        reportTitle="Administrators List"
      />

      <AdminFormatDialog
        isOpen={showFormatDialog}
        onClose={() => {
          setShowFormatDialog(false);
          setSelectedPages(null);
        }}
        onDownload={handleFormatDownload}
        downloadType="list"
      />
    </div>
  );
};

export default AdminsList;
